import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Mic, Square, X } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg } from "../../lib/api";

const CACHE = new Map();
export function loadAuthImg(api, path) {
  if (!CACHE.has(path)) {
    const p = api.get(path, { responseType: "blob" }).then((r) => URL.createObjectURL(r.data));
    p.catch(() => CACHE.delete(path));
    CACHE.set(path, p);
  }
  return CACHE.get(path);
}

export function useAuthImg(path) {
  const api = useApi();
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let live = true;
    if (!path) return undefined;
    setSrc(null);
    loadAuthImg(api, path).then((u) => { if (live) setSrc(u); }).catch(() => { if (live) setSrc("error"); });
    return () => { live = false; };
  }, [api, path]);
  return src;
}

export function AuthImg({ path, alt, className = "", testid, onClick }) {
  const src = useAuthImg(path);
  if (!src) return <div className={`aspect-video grid place-items-center bg-slate-100 rounded-lg ${className}`}><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (src === "error") return <div className={`aspect-video grid place-items-center bg-slate-100 rounded-lg text-xs ${className}`}>Preview unavailable</div>;
  return <img src={src} alt={alt} onClick={onClick} className={`w-full aspect-video object-cover rounded-lg border shadow-sm ${onClick ? "cursor-zoom-in" : ""} ${className}`} data-testid={testid} />;
}

export function Presenter({ paths, start = 0, onClose }) {
  const [i, setI] = useState(start);
  const ref = useRef(null);
  const api = useApi();
  useEffect(() => { paths.forEach((p) => loadAuthImg(api, p).catch(() => {})); }, [api, paths]);
  const go = useCallback((d) => setI((x) => Math.min(paths.length - 1, Math.max(0, x + d))), [paths.length]);
  useEffect(() => {
    const key = (e) => { if (["ArrowRight", " ", "PageDown"].includes(e.key)) go(1); if (["ArrowLeft", "PageUp"].includes(e.key)) go(-1); if (e.key === "Escape") onClose(); };
    let full = false;
    const fs = () => { if (document.fullscreenElement) full = true; else if (full) onClose(); };
    window.addEventListener("keydown", key);
    document.addEventListener("fullscreenchange", fs);
    ref.current?.requestFullscreen?.().catch(() => {});
    return () => { window.removeEventListener("keydown", key); document.removeEventListener("fullscreenchange", fs); if (document.fullscreenElement) document.exitFullscreen().catch(() => {}); };
  }, [go, onClose]);
  return (
    <div ref={ref} className="fixed inset-0 z-[100] bg-black flex items-center justify-center" data-testid="presenter">
      <div className="w-full" style={{ maxWidth: "177.78vh" }}><AuthImg path={paths[i]} alt={`Slide ${i + 1}`} className="!rounded-none !border-0 !shadow-none" testid="presenter-slide" /></div>
      <button onClick={() => go(-1)} disabled={i === 0} className="absolute left-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 text-white disabled:opacity-30" aria-label="Previous slide" data-testid="presenter-prev"><ChevronLeft /></button>
      <button onClick={() => go(1)} disabled={i === paths.length - 1} className="absolute right-3 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 text-white disabled:opacity-30" aria-label="Next slide" data-testid="presenter-next"><ChevronRight /></button>
      <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full bg-white/10 text-white" aria-label="Exit presentation mode" data-testid="presenter-close"><X /></button>
      <span className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/70 text-sm" data-testid="presenter-counter">{i + 1} / {paths.length}</span>
    </div>);
}

export function MicButton({ onText, testid = "mic-btn" }) {
  const api = useApi();
  const [state, setState] = useState("idle");
  const rec = useRef(null);
  useEffect(() => () => { const r = rec.current; if (r && r.state === "recording") { r.onstop = () => r.stream.getTracks().forEach((t) => t.stop()); r.stop(); } }, []);
  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) { toast.error("Voice input isn't supported in this browser. Please type instead."); return; }
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true }); } catch { toast.error("Microphone access was blocked. Allow it in your browser settings, or type instead."); return; }
    const chunks = [];
    const r = new MediaRecorder(stream);
    r.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    r.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      setState("busy");
      try {
        const fd = new FormData();
        fd.append("audio", new Blob(chunks, { type: r.mimeType || "audio/webm" }), "speech.webm");
        const res = await api.post("/presentations/stt", fd);
        onText(res.data.text);
      } catch (e) { toast.error(errMsg(e)); } finally { setState("idle"); }
    };
    rec.current = r; r.start(); setState("rec");
    setTimeout(() => { if (r.state === "recording") r.stop(); }, 120000);
  };
  return (
    <button type="button" onClick={() => (state === "rec" ? rec.current?.stop() : state === "idle" && start())} disabled={state === "busy"}
      className={`chip !p-2 ${state === "rec" ? "!bg-red-600 !text-white animate-pulse" : ""}`} aria-label={state === "rec" ? "Stop recording" : "Speak instead of typing"} title={state === "rec" ? "Stop recording" : "Speak instead of typing"} data-testid={testid}>
      {state === "busy" ? <Loader2 className="w-4 h-4 animate-spin" /> : state === "rec" ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
    </button>);
}
